import { useCallback, useMemo } from 'react';
import { useStore } from '../store';
import { send } from '../ws';
import { getWs } from '../wsConnection';
import { waitForAck } from '../utils/waitForAck';
import { correlationId } from "@interlucent/ids";
import type {
  ProfileMapApplicationReq,
  ProfileUnmapApplicationReq,
  ProfileMapApplicationAck,
  AckPayload
} from '@interlucent/organization-protocol';

/**
 * Profile ↔ application membership operations, backed by materialized pool
 * coverage. An app counts as "mapped" only when the pool's committed
 * (materialized) membership includes it — the same truth the serving path
 * uses. Writes go through the mapping.map / mapping.unmap commands, which
 * create/delete pool inclusions server-side; the resulting
 * AppPoolCoverageChanged event refreshes the store.
 */
export function useProfileMappings(profileId: string, projectId: string) {
  const appPoolCoverage = useStore(state => state.appPoolCoverage);
  const applications = useStore(state => state.applications);

  const isMapped = useCallback((appId: string) => {
    return (appPoolCoverage[appId] ?? []).some(e => e.poolId === profileId);
  }, [appPoolCoverage, profileId]);

  const projectApps = useMemo(() => {
    return Object.values(applications)
      .filter(app => app.projectId === projectId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, projectId]);

  const mappedApplications = useMemo(
    () => projectApps.filter(app => isMapped(app.id)),
    [projectApps, isMapped]
  );

  const availableApplications = useMemo(
    () => projectApps.filter(app => !isMapped(app.id)),
    [projectApps, isMapped]
  );

  const mappingCount = mappedApplications.length;

  /**
   * Map an application to the profile (creates an app-level pool inclusion).
   */
  const addMapping = useCallback(async (
    appId: string,
    versionId?: string,
    settings?: Record<string, any>
  ): Promise<{ success: boolean; mappingId?: string; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileMapApplicationReq = {
        profileId,
        appId,
        versionId,
        settings
      };

      send('mapping.map', payload, corr);

      const ack = await waitForAck<ProfileMapApplicationAck>(corr);

      if (ack.ok) {
        return { success: true, mappingId: ack.mappingId };
      }
      return { success: false, error: 'Failed to map application' };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to map application'
      };
    }
  }, [profileId]);

  /**
   * Remove an application from the profile (deletes its app-level inclusions).
   */
  const removeMapping = useCallback(async (
    appId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileUnmapApplicationReq = {
        profileId,
        appId
      };

      send('mapping.unmap', payload, corr);

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      }
      return { success: false, error: 'Failed to unmap application' };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to unmap application'
      };
    }
  }, [profileId]);

  return {
    mappedApplications,
    availableApplications,
    mappingCount,
    addMapping,
    removeMapping,
    isMapped
  };
}
